import { NetworkStatus } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { useEffect, useRef, useState } from "react";
import { GET_AVAILABLE_SLOTS, type AvailableSlotsData } from "./operations";

/**
 * A successful network read of one selection counts as current for this long:
 * a remount, a re-subscription or a skip toggle inside it reads the cache.
 */
export const RECENT_SLOTS_READ_MS = 5_000;

/**
 * When each selection (professional, service, day) last had a SUCCESSFUL
 * network read, in memory only: never storage. An errored or partial read is
 * never recorded, and clears the selection, so the next mount asks again.
 */
const lastGoodRead = new Map<string, number>();

function selectionKey(professionalId: string, serviceId: string | null, date: string | null): string | null {
  return serviceId && date ? `${professionalId}|${serviceId}|${date}` : null;
}

type Decision = { key: string | null; fetchPolicy: "cache-first" | "cache-and-network" };

// Reads the clock, so it runs only where a decision is made: the useState
// initializer and the selection-change branch below, never on a plain render.
function decide(key: string | null): Decision {
  const last = key ? lastGoodRead.get(key) : undefined;
  const recent = last !== undefined && Date.now() - last < RECENT_SLOTS_READ_MS;
  return { key, fetchPolicy: recent ? "cache-first" : "cache-and-network" };
}

/** Test-only: forget every recorded read. */
export function __resetRecentSlotReadsForTests(): void {
  lastGoodRead.clear();
}

/**
 * The open times for one service on one professional-local day, beside the
 * other booking hooks (c4: moved out of StepTime unchanged).
 *
 * Skipped until both a service and a valid day are chosen. Pass `date` only
 * once it is a day the step offers: an out-of-window or malformed day in a
 * shared URL is not an API date.
 *
 * `cache-and-network`: a slot list is stale the moment it arrives (someone
 * else may be holding one right now), so it is refetched on mount and
 * returning via Back does not offer times that went while the client was on
 * another step. The cached list paints meanwhile; StepTime keeps it inert
 * until the fresh answer lands (optimization review F02).
 *
 * BUT NOT TWICE IN A FEW SECONDS (IF-3, containment). CI once saw the app
 * itself fetch the same day's slots three times on one tap, after the guest's
 * sign-up and lookup; it never reproduced off CI, so its trigger is unknown.
 * The policy is decided once per mount and selection, so a mount, a skip
 * toggle or a return to the selection within RECENT_SLOTS_READ_MS of a
 * SUCCESSFUL network read of it reads the cache instead. A re-subscription
 * inside the mount that made the read is NOT contained (its policy is still
 * cache-and-network), nor is a re-execution while the first read is in
 * flight. After that, Back refetches as before; availability is
 * authoritative at CreateSlotHold in any case. Explicit refetches are
 * untouched: `refetch()` always goes to the network (an expired hold, a
 * taken or gone slot, the user's retry), and starting one forgets the
 * selection's recent read until it succeeds.
 *
 * THE TRIGGER, FOUND (rc.8): the router rendered a tap in a transition, this
 * query refetched during that render, and a fast answer forced an urgent
 * re-render of the old day, which refetched it. Book's router no longer uses
 * transitions (router/transitions.ts,
 * docs/architecture/router-transitions-and-apollo.md); this containment
 * stays as defence in depth.
 *
 * The whole Apollo result is returned: StepTime reads `data`, `loading`,
 * `error` and `refetch` (the plain refetch; its LoadFailed notice wraps it in
 * retryApiRead).
 */
export function useAvailableSlots({
  professionalId,
  serviceId,
  date,
}: {
  professionalId: string;
  serviceId: string | null;
  date: string | null;
}) {
  const key = selectionKey(professionalId, serviceId, date);
  // Decided once per mount and selection (derived state, re-decided only when
  // the selection changes), so recording this mount's own read never changes
  // the policy under the query that made it.
  const [decision, setDecision] = useState<Decision>(() => decide(key));
  let current = decision;
  if (decision.key !== key) {
    current = decide(key);
    setDecision(current);
  }

  const result = useQuery<AvailableSlotsData>(GET_AVAILABLE_SLOTS, {
    variables: { input: { professionalId, serviceId, date } },
    skip: !key,
    fetchPolicy: current.fetchPolicy,
  });

  // When a network read of THIS selection completes, record it only if it
  // succeeded in full. The marker names the selection it was set for: a list
  // another selection reads from the cache while a read is in flight is not a
  // network read, and must not extend its own window (reviewer, 2026-10-13).
  const { networkStatus, error, dataState } = result;
  const fetching =
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.refetch ||
    networkStatus === NetworkStatus.setVariables;
  const inFlight = useRef<string | null>(null);
  useEffect(() => {
    if (!key) return;
    if (fetching) {
      // A read in flight means the cached list is not known to be current
      // (a refetch for an expired hold or a taken or gone slot says it is
      // stale): until this read succeeds, a return to the selection asks the
      // network and shows the list inert (reviewer, 2026-10-13).
      lastGoodRead.delete(key);
      inFlight.current = key;
      return;
    }
    if (inFlight.current !== key) return;
    inFlight.current = null;
    if (!error && networkStatus === NetworkStatus.ready && dataState === "complete") lastGoodRead.set(key, Date.now());
    else lastGoodRead.delete(key);
  }, [key, fetching, error, networkStatus, dataState]);

  return result;
}
